import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Boxes, Radar, ShoppingBag, PieChart as PieIcon } from "lucide-react";
import api from "../lib/api";
import { Card } from "../lib/ui";
import { fmtRp } from "../lib/constants";
import { useT } from "../lib/i18n";

const PALETTE = ["#ea580c", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f43f5e"];

const compactRp = (v) => {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1)}M`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}jt`;
  if (a >= 1e3) return `${Math.round(n / 1e3)}rb`;
  return `${n}`;
};
const shortDate = (d) => (d || "").slice(5);

function WidgetCard({ icon: Icon, title, subtitle, children, span, testid }) {
  return (
    <Card className={`p-4 sm:p-5 ${span || ""}`} data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-light text-primary"><Icon size={16} /></span>
        <div>
          <h3 className="font-heading text-sm font-extrabold text-ink">{title}</h3>
          {subtitle && <p className="text-[11px] text-ink-mute">{subtitle}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
}

const EmptyState = ({ label }) => (
  <div className="flex h-[180px] items-center justify-center text-sm text-ink-mute">{label}</div>
);

export default function CostInsights() {
  const { t } = useT();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/cost-insights").then(({ data }) => setD(data)).catch(() => setD({})); }, []);

  if (!d) return (
    <div className="mt-8 h-40 animate-pulse rounded-3xl bg-stone-100" data-testid="cost-insights-loading" />
  );

  const inv = d.top_inventory_value || [];
  const leak = d.top_leakage_cost || [];
  const spend = d.purchase_spend_trend || [];
  const suppliers = d.supplier_concentration || [];
  const valueTrend = d.inventory_value_trend || [];

  return (
    <div className="mt-8" data-testid="cost-insights">
      <div className="mb-4 flex items-center gap-2">
        <TrendingUp size={18} className="text-primary" />
        <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">{t("Cost Insights")}</h2>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 5. Inventory Value Trend (wide) */}
        <WidgetCard icon={TrendingUp} title={t("Inventory Value Trend")} subtitle={t("Warehouse · last 30 days")} span="lg:col-span-2" testid="ci-value-trend">
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={valueTrend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
              <defs><linearGradient id="ciValueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ea580c" stopOpacity={0.35} /><stop offset="100%" stopColor="#ea580c" stopOpacity={0.02} />
              </linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval={5} />
              <YAxis tickFormatter={compactRp} tick={{ fontSize: 10, fill: "#9b8e82" }} width={42} />
              <Tooltip formatter={(v) => fmtRp(v)} labelFormatter={(l) => l} />
              <Area type="monotone" dataKey="value" stroke="#ea580c" strokeWidth={2.5} fill="url(#ciValueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </WidgetCard>

        {/* 3. 30-Day Purchase Spend */}
        <WidgetCard icon={ShoppingBag} title={t("30-Day Purchase Spend")} subtitle={t("Goods received into warehouse")} testid="ci-purchase-spend">
          {spend.some((x) => x.spend > 0) ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={spend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval={5} />
                <YAxis tickFormatter={compactRp} tick={{ fontSize: 10, fill: "#9b8e82" }} width={42} />
                <Tooltip formatter={(v) => fmtRp(v)} />
                <Bar dataKey="spend" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState label={t("No purchases in the last 30 days")} />}
        </WidgetCard>

        {/* 4. Supplier Spending Concentration */}
        <WidgetCard icon={PieIcon} title={t("Supplier Concentration")} subtitle={t("Share of total purchase spend")} testid="ci-supplier-concentration">
          {suppliers.length ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="55%" height={210}>
                <PieChart>
                  <Pie data={suppliers} dataKey="spend" nameKey="supplier_name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {suppliers.map((s, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtRp(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {suppliers.slice(0, 6).map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`ci-supplier-${i}`}>
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                      <span className="truncate text-ink-soft">{s.supplier_name}</span>
                    </span>
                    <span className="shrink-0 font-bold text-ink">{s.share_pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyState label={t("No supplier spend yet")} />}
        </WidgetCard>

        {/* 1. Top 5 Products by Inventory Value */}
        <WidgetCard icon={Boxes} title={t("Top 5 by Inventory Value")} subtitle={t("Highest-value stock on hand")} testid="ci-top-inventory">
          {inv.length ? (
            <div className="space-y-2.5 py-1">
              {inv.map((p, i) => {
                const max = inv[0].value || 1;
                return (
                  <div key={i} data-testid={`ci-inv-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-semibold text-ink">{p.product_name}</span>
                      <span className="font-bold text-ink">{fmtRp(p.value)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(6, (p.value / max) * 100)}%`, background: PALETTE[i % PALETTE.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyState label={t("No inventory value yet")} />}
        </WidgetCard>

        {/* 2. Top 10 Leakage Cost Products */}
        <WidgetCard icon={Radar} title={t("Top Leakage Cost Products")} subtitle={t("Biggest Rp variance impact (loss + / surplus −)")} span="lg:col-span-2" testid="ci-top-leakage">
          {leak.length ? (
            <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 py-1 sm:grid-cols-2">
              {leak.map((p, i) => {
                const max = Math.max(...leak.map((x) => Math.abs(x.leakage_value))) || 1;
                const loss = p.leakage_value >= 0;
                return (
                  <div key={i} data-testid={`ci-leak-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-semibold text-ink">{p.product_name}</span>
                      <span className={`font-bold ${loss ? "text-danger-text" : "text-success-text"}`}>{loss ? "" : "+"}{fmtRp(Math.abs(p.leakage_value))}{loss ? " " + t("loss") : " " + t("surplus")}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(6, (Math.abs(p.leakage_value) / max) * 100)}%`, background: loss ? "#ef4444" : "#10b981" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyState label={t("No leakage data yet — needs two finalized stock takes")} />}
        </WidgetCard>
      </div>
    </div>
  );
}
